import { useParams, Link } from 'react-router-dom'
import { useCollaborations } from '@/hooks/useCollaborations'
import { useState, useMemo } from 'react'
import { Calendar, Copy, CheckCircle2, Share, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useSubmitLivePostLink } from '@/hooks/useBidMutations'

const FinalVideo = () => {
  const { id } = useParams<{ id: string }>()
  const { collaborations } = useCollaborations()
  const collaboration = useMemo(
    () => collaborations.find((c) => c.id === id),
    [id, collaborations],
  )

  const [copied, setCopied] = useState(false)

  // Modal state for live post link submission
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [livePostLink, setLivePostLink] = useState('')
  const [linkTouched, setLinkTouched] = useState(false)
  const submitLivePostLink = useSubmitLivePostLink()

  // Helper: check if a valid URL
  const isValidUrl = (url: string) => /^https?:\/\/.+\..+/.test(url)

  if (!collaboration) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold mb-4 text-white">Campaign Not Found</h2>
        <p className="mb-4 text-gray-300">The campaign you're looking for doesn't exist.</p>
        <Link to="/" className="text-blue-400 hover:text-blue-300 hover:underline">
          Return to Dashboard
        </Link>
      </div>
    )
  }
  const isCreativeFinalized = !!collaboration.contentApprovedAt
  const postLink = collaboration.postLinks?.[0]

  const suggestedCaption = `${collaboration.brand.name} helps me take my training to the next level! 🔥 #ad #${collaboration.brand.name.replace(/\s+/g, '')}`

  const handleCopyCaption = () => {
    navigator.clipboard.writeText(suggestedCaption)
    setCopied(true)
    toast('Caption copied!', {
      description: 'The suggested caption has been copied to your clipboard.',
    })

    // Reset copied state after 3 seconds
    setTimeout(() => setCopied(false), 3000)
  }

  const handlePostNow = () => {
    // In a real app, this would likely open the native sharing UI or deep link to the platform
    window.open(
      collaboration.platform.includes('TikTok')
        ? 'https://www.tiktok.com/upload'
        : collaboration.platform.includes('Instagram Reels')
          ? 'https://www.instagram.com'
          : 'https://www.youtube.com',
      '_blank',
    )

    toast('Opening posting platform', {
      description: `Redirecting you to ${collaboration.platform} to post your content.`,
    })
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-white">
            {isCreativeFinalized ? 'Your Final Video is Ready' : 'Your Video is Under Review'}
          </h1>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isCreativeFinalized ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {isCreativeFinalized ? 'Approved & Ready to Publish' : 'Pending Review'}
          </span>
        </div>
        <p className="text-gray-300">
          {collaboration.title} • {collaboration.brand.name}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white shadow-sm rounded-xl overflow-hidden mb-6">
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900">Final Video Preview</h2>

              <div className="bg-gray-100 rounded-lg mb-6 flex items-center justify-center max-h-[70vh] overflow-hidden">
                {collaboration.contentApprovedUrl ? (
                  <video
                    src={collaboration.contentApprovedUrl}
                    controls
                    className="w-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="text-center p-4">
                    <div className="w-16 h-16 mx-auto bg-blue-500 rounded-full flex items-center justify-center text-white mb-4">
                      <CheckCircle2 className="h-8 w-8" />
                    </div>
                    <p className="text-gray-500">Video URL not available</p>
                  </div>
                )}
              </div>

              {collaboration.contentApprovedUrl && (
                <div className="mb-6 bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">
                    <strong>To download:</strong> Right-click on the video above and select "Save
                    video as..." to save it to your device.
                  </p>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Caption Notes</h3>
                <div className="relative">
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-gray-700">
                    {suggestedCaption}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute right-2 top-2 flex items-center gap-1"
                    onClick={handleCopyCaption}
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button variant="lime" className="flex-1 gap-2" onClick={handleCopyCaption}>
                  <Copy className="h-4 w-4" />
                  Copy Caption
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-sm rounded-xl overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900">Posting Instructions</h2>

              <ol className="list-decimal list-inside space-y-4 pl-4 text-gray-700">
                <li>Download your final video using the button above.</li>
                <li>Copy the suggested caption provided.</li>
                <li>Post the video to {collaboration.platform} by your scheduled time.</li>
                <li>Add the caption and relevant hashtags.</li>
                <li>Make sure your link is in bio or ask followers to "Comment for link."</li>
              </ol>

              <div className="mt-6 bg-blue-50 p-4 rounded border border-blue-100">
                <h3 className="text-sm font-medium text-blue-800 mb-1">Important Notes</h3>
                <ul className="list-disc list-inside text-blue-700 space-y-1 text-sm">
                  <li>Do not modify the video with additional filters or effects</li>
                  <li>The post must be public and accessible until the campaign end date</li>
                  <li>Payment will be processed after verification of the post</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="bg-white shadow-sm rounded-xl overflow-hidden sticky top-6">
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900">Content Status</h2>

              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Content Submitted</h3>
                    <p className="text-sm text-gray-500">
                      {new Date(collaboration.contentCreatedAt || '').toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${collaboration.contentApprovedAt ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-700'}`}
                  >
                    {collaboration.contentApprovedAt ? <CheckCircle2 className="h-4 w-4" /> : 2}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Creative Finalized</h3>
                    <p className="text-sm text-gray-500">
                      {collaboration.contentApprovedAt
                        ? new Date(collaboration.contentApprovedAt).toLocaleDateString()
                        : 'In Review'}
                    </p>
                  </div>
                </div>

                <div
                  className={`flex items-start gap-3 rounded transition ${
                    isCreativeFinalized && !postLink
                      ? 'cursor-pointer hover:bg-blue-50 bg-blue-25 border-2 border-blue-200 p-3'
                      : postLink
                        ? 'p-3'
                        : ''
                  }`}
                  onClick={() => {
                    if (isCreativeFinalized && !postLink) {
                      setIsModalOpen(true)
                    }
                  }}
                  title={
                    isCreativeFinalized
                      ? postLink
                        ? 'Post link already submitted'
                        : 'Submit your live post link'
                      : 'Creative not yet finalized'
                  }
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5
                    ${postLink ? 'bg-green-500 text-white' : isCreativeFinalized ? 'bg-blue-500 text-white animate-bounce' : 'bg-gray-300 text-gray-700'}`}
                  >
                    {postLink ? <CheckCircle2 className="h-4 w-4" /> : 3}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{postLink ? 'Posted' : 'Post'}</h3>
                    {postLink ? (
                      <p className="text-sm text-green-600 break-all">
                        <a
                          href={postLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          View Post
                        </a>
                      </p>
                    ) : isCreativeFinalized ? (
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-blue-700">
                          📱 After posting, come back and share the link with us
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Waiting for approval</p>
                    )}
                  </div>
                  {isCreativeFinalized && !postLink && (
                    <div className="shrink-0">
                      <Button
                        size="sm"
                        variant="lime"
                        className="animate-pulse"
                        onClick={(e) => {
                          e.stopPropagation()
                          setIsModalOpen(true)
                        }}
                      >
                        Submit Link
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <Link
                  to={`/scheduling/${collaboration.id}`}
                  className="text-blue-500 hover:underline flex items-center gap-1 text-sm"
                >
                  <Calendar className="h-4 w-4" />
                  View Posting Schedule
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modal for submitting live post link */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Live Post Link</DialogTitle>
            <DialogDescription>
              Paste the URL where your post is live. This will be used to verify your campaign
              completion.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              setLinkTouched(true)
              if (!isValidUrl(livePostLink)) {
                toast.error('Invalid URL', {
                  description: 'Please enter a valid URL (must start with http(s)://)',
                })
                return
              }
              try {
                await submitLivePostLink.mutate({ id: collaboration.id, livePostLink })
                toast('Link submitted successfully!')
                setIsModalOpen(false)
                setLivePostLink('')
                setLinkTouched(false)
              } catch (error) {
                toast.error('Submission failed', {
                  description: 'Could not submit link. Please try again.',
                })
              }
            }}
            className="space-y-4"
          >
            <Input
              className="text-gray-900" // Explicitly set text color to dark
              value={livePostLink}
              onChange={(e) => {
                setLivePostLink(e.target.value)
                setLinkTouched(true)
              }}
              placeholder="https://instagram.com/p/..."
              type="url"
              autoFocus
              required
              disabled={submitLivePostLink.isPending}
            />
            {linkTouched && livePostLink && !isValidUrl(livePostLink) && (
              <div className="text-sm text-red-600">
                Please enter a valid URL (must start with http(s)://)
              </div>
            )}
            <DialogFooter>
              <Button
                type="submit"
                disabled={
                  submitLivePostLink.isPending || !livePostLink || !isValidUrl(livePostLink)
                }
                className="w-full"
              >
                {submitLivePostLink.isPending ? 'Submitting...' : 'Submit Link'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default FinalVideo
